import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import { useScrapflyUsage } from '../../hooks/useAdmin';
import { formatDate } from './adminUtils';

export default function AdminScrapflyDetails() {
    const { data: scrapfly, isLoading } = useScrapflyUsage();

    if (isLoading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
    }

    const today = scrapfly?.today;
    const period = scrapfly?.period;
    const recent = scrapfly?.recent ?? [];
    const periodPct = period ? Math.round((period.total_requests / period.monthly_quota) * 100) : 0;
    const periodColor = periodPct >= 90 ? '#e74c3c' : periodPct >= 70 ? '#e67e22' : '#27ae60';

    return (
        <Box sx={{ maxWidth: 1100, mx: 'auto', px: 2, py: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50', mb: 2 }}>
                ScrapFly potrošnja
            </Typography>

            {/* Today stats */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Danas</Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Chip label={`Ukupno: ${today?.total_requests ?? 0}`} />
                    <Chip label={`Uspješno: ${today?.success ?? 0}`} color="success" variant="outlined" />
                    <Chip label={`Neuspješno: ${today?.fail ?? 0}`} color="error" variant="outlined" />
                    {today?.avg_response_time_ms != null && (
                        <Chip label={`Prosječno: ${today.avg_response_time_ms}ms`} variant="outlined" />
                    )}
                </Box>
            </Paper>

            {/* Period stats */}
            {period && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Billing period (od {period.period_start})
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">
                            <b>{period.total_requests.toLocaleString()}</b> / {period.monthly_quota.toLocaleString()} zahtjeva
                        </Typography>
                        <Typography variant="body2" sx={{ color: periodColor, fontWeight: 600 }}>{periodPct}%</Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={Math.min(periodPct, 100)}
                        sx={{ height: 8, borderRadius: 4, backgroundColor: '#eee', '& .MuiLinearProgress-bar': { backgroundColor: periodColor } }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {period.days_elapsed} dana prošlo · {period.days_remaining} dana preostalo · Prosjek: {period.daily_average}/dan · Projekcija: {period.projected_total.toLocaleString()}
                    </Typography>
                </Paper>
            )}

            {/* Recent requests */}
            {recent.length > 0 && (
                <Paper sx={{ overflow: 'auto' }}>
                    <Typography variant="subtitle2" sx={{ px: 2, pt: 2, fontWeight: 600 }}>
                        Zadnjih 20 zahtjeva
                    </Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell sx={{ fontWeight: 600 }}>Vrijeme</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>URL</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Vrijeme (ms)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {recent.map((r) => (
                                <TableRow key={r.id} hover>
                                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 13 }}>{formatDate(r.timestamp)}</TableCell>
                                    <TableCell sx={{ fontSize: 12, maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.url}</TableCell>
                                    <TableCell>
                                        <Typography variant="caption" sx={{ color: r.success ? '#27ae60' : '#e74c3c', fontWeight: 600 }}>
                                            {r.success ? 'OK' : 'FAIL'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 13 }}>{r.response_time_ms ?? '—'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            )}
        </Box>
    );
}
